import { useEffect, useContext, useState } from "react";
import React from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";
import MenuItem from "./components/menuitem";
import SubMenuItem from "./components/subitem";

import { adminKey } from "services/config";

// @mui material components
import {
  Divider,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Collapse,
} from "@mui/material";

// Kaizen Dashboard components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Kaizen Dashboard examples
import SidenavCollapse from "components/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "components/Sidenav/SidenavRoot";
import sidenavLogoLabel from "components/Sidenav/styles/sidenav";

// Kaizen Dashboard context
import { useSoftUIController, setMiniSidenav } from "context";
import { UserContext } from "context/user";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];

  // Agregar el estado para controlar el menú colapsable abierto
  const [openCollapse, setOpenCollapse] = useState(null);

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    window.addEventListener("resize", handleMiniSidenav);

    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const renderRoutes = routes.map(
    ({ type, name, icon, title, noCollapse, key, route, href, access, subitems }) => {
      let returnValue;
      const user = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null;
      if (type === "collapse" && subitems) {
        const isOpen = openCollapse === key;

        returnValue = (
          <React.Fragment key={key}>
            <MenuItem
              icon={icon}
              name={name}
              route={route}
              // selected={key === collapseName}
              color={color}
              // active={key === collapseName}
              noCollapse={noCollapse}
              open={isOpen}
              onClick={() => setOpenCollapse(isOpen ? null : key)}
            />
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List disablePadding sx={{ overflow: "hidden" }}>
                {subitems.map(({ type, name, icon, key, route, subitemAccess }) => {
                  if (user) {
                    if (subitemAccess?.includes(user.id_rol)) {
                      return (
                        <NavLink to={route} key={key}>
                          <SubMenuItem
                            icon={icon}
                            name={name}
                            route={route}
                            selected={key === pathname.split("/")[2]}
                            key={key}
                            color={color}
                            active={key === collapseName}
                          />
                        </NavLink>
                      );
                    }
                  }
                })}
              </List>
            </Collapse>
          </React.Fragment>
        );
      } else if (type === "collapse" && !subitems) {
        returnValue = returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              color={color}
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          </Link>
        ) : (
          <NavLink to={route} key={key}>
            <SidenavCollapse
              color={color}
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          </NavLink>
        );
      } else if (type === "title") {
        returnValue = (
          <SoftTypography
            key={key}
            display="block"
            variant="subtitle2"
            fontWeight="bold"
            textTransform="uppercase"
            opacity={0.6}
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </SoftTypography>
        );
      } else if (type === "divider") {
        returnValue = <Divider key={key} />;
      }

      if (user) {
        if (access?.includes(user.id_rol)) {
          return returnValue;
        } else if (user.adminKey === adminKey) {
          return returnValue;
        }
      }
      return null;
    }
  );

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
      <SoftBox pt={3} pb={1} px={4} textAlign="center">
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SoftTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </SoftTypography>
        </SoftBox>
        <SoftBox display="flex" alignItems="center">
          {brand && <SoftBox component="img" src={brand} alt="Kaizen Logo" width="100%" />}
        </SoftBox>
      </SoftBox>
      <Divider />
      <List sx={{ overflow: "auto" }}>{renderRoutes}</List>
      <SoftBox pt={2} my={2} mx={2} mt="auto">
        {/* <SoftBox mt={2}>
          <SoftButton
            component="a"
            href="https://wa.link/gh2iw6"
            target="_blank"
            rel="noreferrer"
            variant="gradient"
            color={color}
            fullWidth
          >
            <Icon sx={{ fontWeight: "bold" }}>mobile_friendly</Icon>
            &nbsp; Contactanos
          </SoftButton>
        </SoftBox> */}
      </SoftBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "primary",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
